/**
 * @fileoverview Response helpers for the audit mock endpoints.
 * @description Builds the backend's success and error envelopes for the audit mocks, including the
 * paginated envelope whose `meta` also carries `searchScope`. Never imported outside this
 * feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { AuditSearchScope } from '@/features/audit/types/audit.types'
import type { AxiosRequestConfig } from 'axios'

/**
 * @description Wraps a payload in the backend's success envelope.
 * @param {unknown} data - The payload.
 * @param {object} [meta] - The paginated list's meta.
 * @returns {object} The envelope.
 */
export function ok(data: unknown, meta?: object) {
  return {
    httpStatus: 200,
    message: 'OK',
    traceId: crypto.randomUUID(),
    data,
    ...(meta && { meta }),
  }
}

/**
 * @description Builds the backend's error envelope.
 * @param {number} httpStatus - The HTTP status.
 * @param {string} appCode - The application error code.
 * @param {string} message - The message.
 * @returns {[number, object]} The mock reply.
 */
export function fail(httpStatus: number, appCode: string, message: string): [number, object] {
  return [
    httpStatus,
    { httpStatus, httpError: '', appCode, message, traceId: crypto.randomUUID(), data: [] },
  ]
}

/**
 * @description Reads a request's query params.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {Record<string, string | undefined>} The params.
 */
export function queryParams(config: AxiosRequestConfig): Record<string, string | undefined> {
  return (config.params ?? {}) as Record<string, string | undefined>
}

/**
 * @description Replies with one page of events, in the backend's paginated envelope with
 * `meta.searchScope`.
 * @param {Record<string, string | undefined>} params - The request's query params (page, limit).
 * @param {T[]} all - Every matching item.
 * @param {AuditSearchScope} searchScope - The scope that was searched.
 * @returns {[number, object]} The mock reply.
 */
export function pageWithScope<T>(
  params: Record<string, string | undefined>,
  all: T[],
  searchScope: AuditSearchScope,
): [number, object] {
  const page = Math.max(1, Number(params.page) || 1)
  const limit = Math.min(100, Math.max(1, Number(params.limit) || 20))
  const items = all.slice((page - 1) * limit, page * limit)
  return [200, ok(items, { page, limit, total: all.length, searchScope })]
}
